import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService, Product } from '../services/api';
import { useCart } from '../context/CartContext';
import QuickAddToCartModal from './QuickAddToCartModal';

const NewArrivals: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<Product[]>([]);
  const { addToCart } = useCart();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.getProducts();
        const all: Product[] = res?.data || [];
        // Only active products, sort by created_at desc, take latest 4
        const sorted = all
          .filter(p => p.is_active)
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
          .slice(0, 4);
        if (mounted) setItems(sorted);
      } catch (e) {
        if (mounted) setError('Failed to load new arrivals');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const formatKyats = (price: number) => `${Number(price).toLocaleString("en-US")} Kyats`;

  return (
    <section className="py-16 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-medium text-gray-500 tracking-wider uppercase mb-2">
            TOP TRENDING
          </p>
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            New Arrivals
          </h2>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="py-8 text-center text-gray-500">Loading new arrivals...</div>
        )}
        {!loading && error && (
          <div className="py-8 text-center text-red-600">{error}</div>
        )}

        {/* Products Grid */}
        {!loading && !error && items.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
              {items.map((p) => {
                const primaryImg = p.product_images?.[0];
                const hoverImg = p.hover_image || p.product_images?.[1];
                const compare = p.compare_at_price;
                
                return (
                  <div
                    key={p.id}
                    className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
                  >
                    <div className="relative overflow-hidden">
                      <Link to={`/products/${p.slug}`} className="block">
                      {/* Product Images */}
                      <div className="relative h-48 sm:h-80">
                        {primaryImg && (
                          <img
                            loading="lazy"
                            width="400"
                            height="400"
                            src={primaryImg}
                            alt={p.name}
                            className={`w-full h-48 sm:h-full object-cover transition-opacity duration-500 ${
                              hoverImg ? 'group-hover:opacity-0' : 'group-hover:scale-105'
                            }`}
                          />
                        )}
                        {hoverImg && (
                          <img
                            loading="lazy"
                            width="400"
                            height="400"
                            src={hoverImg}
                            alt={`${p.name} - Alternative view`}
                            className="absolute inset-0 w-full h-48 sm:h-80 object-cover transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                          />
                        )}
                        {/* New Badge */}
                        <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                          <span className="bg-green-600 text-white px-2 py-0.5 sm:px-3 sm:py-1 text-xs font-bold rounded-full shadow-lg">NEW</span>
                        </div>
                        
                        {/* Discount Badge - Left Side */}
                        {(p.has_promotion || (p.promotional_price && p.promotional_price < p.price) || (p.discounted_price && p.discounted_price < p.price)) && p.discount_percentage && (
                          <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
                            <span className="bg-orange-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 text-[10px] sm:text-xs font-semibold rounded shadow">
                              -{Math.round(p.discount_percentage)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </Link>

                      {/* Hover Actions */}
                      <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                        <button
                          className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (p.variants && p.variants.length > 0) {
                              setQuickAddProduct(p);
                              setQuickAddOpen(true);
                            } else {
                              addToCart(p);
                            }
                          }}
                          title="Add to cart"
                        >
                          <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6m0 0h12.5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                        
                        <Link to={`/products/${p.slug}`} className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-600 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg" title="View details">
                          <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </Link>
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="p-3 sm:p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-lg line-clamp-2">
                        {p.name}
                      </h3>
                      
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <span className="text-sm sm:text-lg font-bold text-gray-900">
                          {(p.has_promotion || (p.promotional_price && p.promotional_price < p.price))
                            ? formatKyats(p.promotional_price || p.price)
                            : (p.discounted_price && p.discounted_price < p.price)
                            ? formatKyats(p.discounted_price || p.price)
                            : formatKyats(p.price)
                          }
                        </span>
                        {((p.has_promotion || (p.promotional_price && p.promotional_price < p.price)) || (p.discounted_price && p.discounted_price < p.price)) && (
                          <span className="text-sm text-gray-500 line-through">{formatKyats(p.price)}</span>
                        )}
                        {!p.has_promotion && !p.promotional_price && !p.discounted_price && compare && compare > p.price && (
                          <span className="text-sm text-gray-500 line-through">{formatKyats(compare)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View All Button */}
            <div className="text-center">
              <Link
                to="/product-signature?new=1"
                className="inline-block bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-900 transition-colors duration-300"
              >
                View All
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Quick Add To Cart Modal */}
      <QuickAddToCartModal
        isOpen={quickAddOpen}
        product={quickAddProduct}
        onClose={() => { setQuickAddOpen(false); setQuickAddProduct(null); }}
        onConfirm={(sel) => {
          if (!quickAddProduct) return;
          addToCart(quickAddProduct, sel.quantity, {
            variantId: sel.variantId,
            sizeName: sel.sizeName,
            unitPrice: sel.unitPrice,
            compareAtPrice: sel.compareAtPrice,
            inStock: sel.inStock,
            colorName: sel.colorName,
            colorCode: sel.colorCode,
          });
          setQuickAddOpen(false);
          setQuickAddProduct(null);
        }}
      />
    </section>
  );
};

export default NewArrivals;
